import { useMemoizedFn } from 'ahooks';
import dayjs from 'dayjs';
import { find } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Loading } from 'tdesign-react';
import { isVoid } from '@/utils/common';
import { toDayjs } from '../LineChart';
import { formatter } from '..';

export default function AreaChart({
  data,
  isGroup,
  color,
  unitKey,
  doParse,
  isRate,
  name,
  colors,
  groupLength = 1,
  currentOrder, // 当前排序，数据量过大延迟渲染用
  loading,
  onBrush,
}) {
  const ref = useRef();
  const drawRef = useRef();
  const [isReady, setIsReady] = useState(false);

  const doInitDraw = useMemoizedFn(async () => {
    const { Area, G2 } = await import('@antv/g2plot');
    const config = {
      data: [],
      padding: 'auto',
      xField: 'time', // 不能字符串，用原始time
      yField: 'valueFmt',
      interactions: [{ type: 'brush-x' }],
      yAxis: {
        grid: {
          line: {
            style: {
              lineDash: [4, 3],
            },
          },
        },
      },
      xAxis: {
        label: {
          formatter: (text) => {
            return find(data, { time: Number(text) })?.timeStr;
          },
        },
      },
      tooltip: {
        customContent: (title, datas) => {
          let str = ``;
          for (const item of datas) {
            let val;
            const value = item?.data.value;
            // 如果value是空，直接返回 '-'
            if (isVoid(value)) {
              val = '-';
            } else {
              const parse = doParse(unitKey, item?.data.value);
              val = parse.value + parse.unit;
              if (isRate) {
                val = `${parse.value + parse.unit}/s`;
              }
            }
            str += `<div>${isGroup ? item.name : name}：${val}</div>`;
          }
          return `
        <div class="chart-tool-tip">
          ${str}
          <div >时间：${dayjs(datas[0]?.data.time * 1000).format(formatter)}</div>
        </div>
        `;
        },
      },
      smooth: true,
      autoFit: true,
    };
    const area = new Area(ref.current, config);
    area.render();
    area.on(G2.BRUSH_FILTER_EVENTS.AFTER_FILTER, () => {
      // after brush filter
      const filteredData = area.chart.getData();
      const releases = filteredData.map((d) => d.timeStr);
      onBrush([toDayjs(releases[0]), toDayjs(releases[releases.length - 1])]);
    });
    drawRef.current = area;
    setIsReady(true);
  });

  useEffect(() => {
    doInitDraw();
    return () => {
      drawRef.current?.destroy();
    };
  }, [doInitDraw]);

  useEffect(() => {
    if (isReady) {
      let setting = {
        color,
      };
      if (isGroup) {
        setting = {
          color: colors,
          seriesField: 'seriesField',
          line: {
            style: {
              lineWidth: 0.6,
            },
          },
          areaStyle: {
            fillOpacity: 0.2,
          },
          legend: {
            layout: 'horizontal',
            itemHeight: 5,
            reversed: true,
            ...(groupLength > 3
              ? {
                  position: 'bottom-left',
                  offsetY: 9,
                }
              : {
                  position: 'top-right',
                }),
            flipPage: false,
            marker: {
              style: {
                lineWidth: 4,
                r: 2,
              },
              symbol: 'circle',
            },
            itemName: {
              style: {
                fontSize: 10,
                fill: '#8D9092',
              },
            },
            itemSpacing: 0,
          },
        };
      }
      const config = {
        data,
        animation: data?.length < 200,
        ...setting,
      };
      setTimeout(() => drawRef.current.update(config), currentOrder);
    }
  }, [data, groupLength, color, colors, isGroup, isReady, currentOrder]);

  return (
    <Root>
      <Loading style={{ height: '100%', width: '100%' }} size='small' loading={loading}>
        <Chart ref={ref} />
      </Loading>
    </Root>
  );
}

const Root = styled.div`
  flex: 1;
  height: 0;
  width: 100%;
  .t-loading__parent {
    height: 100%;
  }
`;

const Chart = styled.div`
  width: 100%;
  height: 100%;
`;
